import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import IconButton, {
  ICON_BUTTON_ICONS,
  ICON_BUTTON_TYPES
} from '../../../../components/buttons/IconButton';
import { COLUMN_LABEL_BY_KEY } from '../constants/columnFilterTypes';
import PillModeToggle from './PillModeToggle';

/**
 * TD-2456 — the pill-filter rail for the search-results List view.
 *
 * Renders one group per active keyword-cloud column (Industry / Value chain /
 * Product portfolio): the column label, an optional All/Any match-mode toggle,
 * and one removable chip per clicked pill value. Within a column the toggle
 * maps to the engine's per-column `mode` — All = AND (must have every value),
 * Any = OR (has any). Across columns the engine always ANDs, so there is no
 * cross-column toggle. The toggle only appears once a column has 2+ values
 * (mode is meaningless for a single value).
 *
 * Chips reuse the shared `.filters-chips-item` styling; this component owns the
 * grouping + toggle layout (the flat `FiltersChipsList` primitive stays as-is
 * for its other consumers, e.g. KeyContactsTab).
 */
const FilterChipRail = observer(
  ({ columnFilters, onSetColumnFilter, onClearColumnFilter, onSetPillMode, onClearAll }) => {
    const groups = buildKeywordGroups(columnFilters);
    if (!groups.length) return null;

    const totalChips = groups.reduce((sum, g) => sum + g.values.length, 0);

    return (
      <div className="filters-chips-list-main-wrap">
        <div className="filters-chips-list-text">Filters ({totalChips})</div>
        <div className="filters-chips-list">
          {groups.map((group) => (
            <div className="filter-chip-group" key={group.colKey}>
              <span className="filter-chip-group-label">{group.label}</span>
              {group.values.length >= 2 && (
                <PillModeToggle
                  mode={group.mode}
                  onSetMode={(m) => onSetPillMode(group.colKey, m)}
                />
              )}
              {group.values.map((value) => (
                <div className="filters-chips-item noselect" key={value}>
                  <div className="display-text">{value}</div>
                  <IconButton
                    icon={ICON_BUTTON_ICONS.X_LG}
                    type={ICON_BUTTON_TYPES.BLUE}
                    withFill={false}
                    withBorder={false}
                    onClick={() =>
                      removeKeyword(
                        group.colKey,
                        columnFilters[group.colKey],
                        value,
                        onSetColumnFilter,
                        onClearColumnFilter
                      )
                    }
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
        {onClearAll && (
          <button type="button" className="filters-chips-clear-all" onClick={onClearAll}>
            Clear all
          </button>
        )}
      </div>
    );
  }
);

/**
 * Build one group per active keyword-cloud column.
 * @returns {Array<{ colKey: string, label: string, mode: 'and'|'or', values: string[] }>}
 */
function buildKeywordGroups(columnFilters) {
  const groups = [];
  if (!columnFilters) return groups;
  for (const colKey of Object.keys(columnFilters)) {
    const filter = columnFilters[colKey];
    if (!filter?.keywords?.size) continue;
    groups.push({
      colKey,
      label: COLUMN_LABEL_BY_KEY[colKey] ?? colKey,
      // Fallback for a mode-less filter must match the engine, which treats a
      // missing mode as 'or' (exploreFilterEngine). Defaulting to 'and' here
      // would light the "All" toggle while the engine matched "Any".
      mode: filter.mode || 'or',
      values: [...filter.keywords]
    });
  }
  return groups;
}

/**
 * Remove one keyword from a column's keyword-cloud filter, preserving the mode.
 * Clearing the last keyword removes the column filter entirely.
 */
function removeKeyword(colKey, filter, keyword, onSetColumnFilter, onClearColumnFilter) {
  if (!filter?.keywords) return;
  const nextKeywords = new Set(filter.keywords);
  nextKeywords.delete(keyword);
  if (nextKeywords.size === 0) {
    onClearColumnFilter(colKey);
    return;
  }
  onSetColumnFilter(colKey, { keywords: nextKeywords, mode: filter.mode || 'or' });
}

FilterChipRail.propTypes = {
  columnFilters: PropTypes.object,
  onSetColumnFilter: PropTypes.func.isRequired,
  onClearColumnFilter: PropTypes.func.isRequired,
  onSetPillMode: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired
};

export default FilterChipRail;

// Exported for tests.
export { buildKeywordGroups };
